import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { normalizeCarImages } from "./dummyCarImages";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { getCars } from "../../../services/travel/carService";
import { Car } from "./car.types";
import HireCarModal from "./HireCarModal";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { PickDenguShareButton } from "../../Businesses/BusinessQrShareActions";

const fallbackCenter = { lat: -13.9626, lng: 33.7741 };
const cardsBorderRadii ="8px";

const PICKDENGU_PUBLIC_WEB_URL = (
  process.env.REACT_APP_PUBLIC_WEB_URL ||
  process.env.REACT_APP_FRONTEND_URL ||
  process.env.REACT_APP_APP_URL ||
  "https://pickdengu.mw"
).replace(/\/+$/, "");

const CarDetailPage = () => {
  const { theme } = useCustomTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as { state?: { car?: Car } };

  const [car, setCar] = useState<Car | null>(state?.car ?? null);
  const [loading, setLoading] = useState(!state?.car);
  const [active, setActive] = useState(0);

  const {
    isOpen: isHireModalOpen,
    onOpen: onOpenHireModal,
    onClose: onCloseHireModal,
  } = useDisclosure();

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  useEffect(() => {
    if (state?.car) {
      setCar(state.car);
      setLoading(false);
    }
  }, [state?.car, id]);

  useEffect(() => {
    if (!id) return;

    if (
      state?.car &&
      String((state.car as any)._id ?? (state.car as any).id) === String(id)
    ) {
      return;
    }

    const fetchCar = async () => {
      setLoading(true);
      try {
        const response = await getCars();
        const data = response.data || [];
        const found = data.find(
          (c: any) =>
            String(c._id) === String(id) || String(c.id) === String(id),
        );

        setCar(found ?? null);
      } catch (error) {
        console.error("Failed to fetch car details:", error);
        setCar(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id, state?.car]);

  const images = useMemo(
    () =>
      car ? normalizeCarImages((car as any).images, (car as any).imageUrl) : [],
    [car],
  );

  useEffect(() => {
    setActive(0);
  }, [car?._id, (car as any)?.id]);

  const next = () =>
    setActive((p) => (images.length ? (p + 1) % images.length : 0));
  const prev = () =>
    setActive((p) =>
      images.length ? (p - 1 + images.length) % images.length : 0,
    );

  const pickupCoords = useMemo(() => {
    const c = (car as any)?.pickupLocationCoords;
    if (c && typeof c.lat === "number" && typeof c.lng === "number") {
      return { lat: c.lat, lng: c.lng };
    }
    return null;
  }, [car]);

  const center = pickupCoords || fallbackCenter;
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const t = window.setTimeout(() => {
      try {
        // @ts-ignore
        window.google?.maps?.event?.trigger(mapRef.current, "resize");
        mapRef.current?.panTo(center);
      } catch {}
    }, 180);

    return () => window.clearTimeout(t);
  }, [isLoaded, center]);

  if (loading) {
    return (
      <Box p={6} color={theme.text}>
        <HStack>
          <GlobalSpinner />
          <Text>Loading car details...</Text>
        </HStack>
      </Box>
    );
  }

  if (!car) {
    return (
      <Box p={6} color={theme.text}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={5}
          boxShadow={theme.shadowSm}
        >
          <Text mb={4} color={theme.text}>
            No car details found.
          </Text>
          <Button
            onClick={() => navigate("/hire/cars")}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            boxShadow={theme.shadowSm}
          >
            Back to Cars
          </Button>
        </Box>
      </Box>
    );
  }

  const manuallyAvailable =
    (car as any).availability !== false &&
    (car as any).isMaintenance !== true;

  const hasSelectedDates = Boolean(
    (car as any).selectedPickupDate && (car as any).selectedReturnDate,
  );
  const selectedDatesUnavailable =
    hasSelectedDates && (car as any).checkoutCanHire === false;
  const canHire = manuallyAvailable && !selectedDatesUnavailable;
  const needsDates = manuallyAvailable && !hasSelectedDates;

  const availabilityStatusLabel = String(
    (car as any).availabilityStatusLabel ||
      (needsDates ? "Select dates" : canHire ? "Available" : "Unavailable"),
  );

  const unavailableReason = String(
    (car as any).unavailableReason ||
      (needsDates
        ? "Select pickup and return dates in the Hire window before continuing."
        : canHire
          ? ""
          : "This car cannot be hired for the selected dates."),
  );

  const carShareId = String(
    (car as any)?._id || (car as any)?.id || id || "",
  ).trim();

  const carShareName =
    [(car as any)?.brand || (car as any)?.make, (car as any)?.model]
      .filter(Boolean)
      .join(" ")
      .trim() || "Car hire";

  const carShareUrl = carShareId
    ? `${PICKDENGU_PUBLIC_WEB_URL}/travel/cars/${encodeURIComponent(
        carShareId,
      )}`
    : PICKDENGU_PUBLIC_WEB_URL;

  const carShareText = [
    carShareName,
    (car as any)?.year,
    (car as any)?.category,
    (car as any)?.transmission,
    `MWK ${Number((car as any)?.pricePerDay || 0).toLocaleString()} per day`,
    (car as any)?.pickupLocation,
    (car as any)?.description?.trim()?.slice(0, 160),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <Box p={{ base: 4, md: 6 }} color={theme.text}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="start" flexWrap="wrap">
          <VStack align="start" spacing={1}>
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              color={theme.text}
            >
              {(car as any).brand || (car as any).make} {(car as any).model}
            </Text>
            <Text color={theme.textSubtle}>
              {(car as any).pickupLocation || "-"}
            </Text>
          </VStack>

          <Badge
            colorScheme={canHire && !needsDates ? "green" : "orange"}
            borderRadius={cardsBorderRadii}
            px={3}
            py={1}
          >
            {availabilityStatusLabel}
          </Badge>
        </HStack>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <VStack align="stretch" spacing={3}>
            <Box
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              height={{ base: "260px", md: "420px" }}
              bg={theme.surfaceAlt}
            >
              <Image
                src={images[active]}
                alt="main"
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>

            <HStack justify="space-between">
              <Button
                variant="outline"
                onClick={prev}
                borderColor={theme.borderStrong}
              >
                Prev
              </Button>
              <Text fontSize="sm" color={theme.textMuted}>
                {images.length ? active + 1 : 0} / {images.length}
              </Text>
              <Button
                variant="outline"
                onClick={next}
                borderColor={theme.borderStrong}
              >
                Next
              </Button>
            </HStack>

            <HStack spacing={2} overflowX="auto" py={1}>
              {images.map((src, idx) => (
                <Box
                  key={`${src}-${idx}`}
                  borderWidth={idx === active ? "2px" : "1px"}
                  borderColor={idx === active ? theme.primary : theme.border}
                  borderRadius={cardsBorderRadii}
                  overflow="hidden"
                  cursor="pointer"
                  flex="0 0 auto"
                  onClick={() => setActive(idx)}
                  bg={theme.surface}
                >
                  <Image
                    src={src}
                    alt={`thumb-${idx}`}
                    boxSize="62px"
                    objectFit="cover"
                  />
                </Box>
              ))}
            </HStack>
          </VStack>

          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between" align="start" flexWrap="wrap">
              <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
                MWK {Number((car as any).pricePerDay || 0).toLocaleString()}{" "}
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="normal"
                  color={theme.textSubtle}
                >
                  / day
                </Text>
              </Text>

              <HStack spacing={2} flexWrap="nowrap">
                <Button
                  w="150px"
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients.primaryButton}
                  _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                  isDisabled={!canHire}
                  onClick={() => {
                    if (!canHire) return;
                    onOpenHireModal();
                  }}
                  boxShadow={theme.shadowSm}
                  whiteSpace="nowrap"
                  opacity={canHire ? 1 : 0.65}
                >
                  {canHire
                    ? needsDates
                      ? "Choose dates"
                      : "Hire"
                    : availabilityStatusLabel}
                </Button>

                <PickDenguShareButton
                  shareTitle={`${carShareName} on PickDengu`}
                  shareText={carShareText}
                  shareUrl={carShareUrl}
                  modalTitle={`Share ${carShareName}`}
                  modalDescription="Share this car-hire details page."
                  buttonProps={{
                    w: "115px",
                    variant: "outline",
                    borderColor: theme.borderStrong,
                    color: theme.text,
                    bg: theme.surface,
                    boxShadow: theme.shadowSm,
                    borderRadius: {cardsBorderRadii},
                    _hover: {
                      bg: theme.hover,
                    },
                  }}
                />
              </HStack>
            </HStack>

            {(!canHire || needsDates) && (
              <HStack
                align="start"
                spacing={3}
                border="1px solid"
                borderColor="orange.300"
                bg={theme.name === "dark" ? "orange.900" : "orange.50"}
                borderRadius={cardsBorderRadii}
                p={3}
              >
                <Badge
                  colorScheme="orange"
                  borderRadius={cardsBorderRadii}
                  px={2.5}
                  py={1}
                  flexShrink={0}
                >
                  {availabilityStatusLabel}
                </Badge>
                <Text
                  color={theme.name === "dark" ? "orange.100" : "orange.800"}
                  fontSize="sm"
                  fontWeight="600"
                >
                  {unavailableReason}
                </Text>
              </HStack>
            )}

            <Text color={theme.textMuted}>
              {(car as any).description || "No description provided."}
            </Text>

            <Divider borderColor={theme.divider} />

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <Info label="Make" value={(car as any).make} />
              <Info label="Brand" value={(car as any).brand} />
              <Info label="Model" value={(car as any).model} />
              <Info label="Year" value={(car as any).year?.toString()} />
              <Info label="Seats" value={(car as any).seats?.toString()} />
              <Info label="Category" value={(car as any).category || "Other"} />
              <Info label="Transmission" value={(car as any).transmission} />
            </SimpleGrid>

            <Divider borderColor={theme.divider} />

            <Box
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
            >
              <Box
                px={3}
                py={2}
                bg={theme.surfaceAlt}
                borderBottomWidth="1px"
                borderColor={theme.border}
              >
                <Text fontWeight="semibold" color={theme.text}>
                  Pickup location
                </Text>
                <Text fontSize="sm" color={theme.textMuted}>
                  {(car as any).pickupLocation || "—"}
                </Text>
              </Box>

              {!API_KEY ? (
                <Box p={3}>
                  <Text fontSize="sm" color={theme.danger}>
                    Missing REACT_APP_GOOGLE_MAPS_API_KEY — cannot load map.
                  </Text>
                </Box>
              ) : loadError ? (
                <Box p={3}>
                  <Text fontSize="sm" color={theme.danger}>
                    Maps failed to load.
                  </Text>
                </Box>
              ) : !isLoaded ? (
                <Flex align="center" justify="center" h="220px" gap={2}>
                  <GlobalSpinner />
                  <Text fontSize="sm" color={theme.textSubtle}>
                    Loading map…
                  </Text>
                </Flex>
              ) : (
                <Box h="150px" w="100%">
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={center}
                    zoom={pickupCoords ? 15 : 11}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                      clickableIcons: false,
                    }}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                    onUnmount={() => {
                      mapRef.current = null;
                    }}
                  >
                    {pickupCoords && <MarkerF position={pickupCoords} />}
                  </GoogleMap>
                </Box>
              )}
            </Box>

            <Button
              onClick={() => navigate("/hire/cars")}
              variant="outline"
              borderColor={theme.borderStrong}
              color={theme.text}
              _hover={{ bg: theme.hover }}
              alignSelf="flex-start"
            >
              Back
            </Button>
          </VStack>
        </SimpleGrid>
      </VStack>

      <HireCarModal
        isOpen={isHireModalOpen}
        onClose={onCloseHireModal}
        car={car}
      />
    </Box>
  );
};

function Info({ label, value }: { label: string; value?: string }) {
  const { theme } = useCustomTheme();
  return (
    <Box
      borderWidth="1px"
      borderColor={theme.border}
      borderRadius={cardsBorderRadii}
      p={3}
      bg={theme.surface}
    >
      <Text fontSize="xs" color={theme.textSubtle}>
        {label}
      </Text>
      <Text fontWeight="semibold" color={theme.text}>
        {value || "—"}
      </Text>
    </Box>
  );
}

export default CarDetailPage;
